import dayjs from '@/helpers/dayjs';

class CalendarStore {
  constructor({ datepicked, period, hours }) {
    this.period = { ...period };
    this.datepicked = [...datepicked];
    // WEA-163 - nombre d'heures voulu par date en recherche a l'heure, indexe par la
    // meme cle que `period`. Une date peut etre selectionnee sans heures : le lieu
    // remonte alors des qu'il lui reste une tranche.
    this.hours = { ...(hours || {}) };
  }

  getPeriod() {
    return this.period || {};
  }

  getDatesPicked() {
    return this.datepicked || [];
  }

  setPeriod(value) {
    this.period = value;
    return this;
  }

  setDatesPicked(value) {
    this.datepicked = value;
    return this;
  }

  getHours() {
    return this.hours || {};
  }

  /** `null`/vide retire la contrainte d'heures sur cette date, sans la deselectionner. */
  setHoursForDate(dateKey, value) {
    const next = { ...(this.hours || {}) };
    const parsed = Number(value);
    if (value === '' || value == null || !Number.isFinite(parsed) || parsed <= 0) {
      delete next[dateKey];
    } else {
      next[dateKey] = parsed;
    }
    this.hours = next;
    return this;
  }

  /*
    WEA-163 — creneau voulu pour une date (`{ start, end }` en "HH:00"). La recherche ne
    connait aucun lieu, donc aucune disponibilite : ce creneau est un SOUHAIT. Le filtre
    serveur n'en retient que la duree ; le creneau lui-meme sert au report vers la fiche,
    ou il est confronte aux tranches reellement libres.
  */
  setSlotForDate(dateKey, { start, end }) {
    const s = Number.parseInt(String(start ?? '').slice(0, 2), 10);
    const e = Number.parseInt(String(end ?? '').slice(0, 2), 10);
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return this;

    this.hours = { ...(this.getHours() || {}), [dateKey]: { start, end, hours: e - s } };
    return this;
  }

  /** Retire le creneau : la date repasse en periode (journee / matin / apres-midi). */
  removeSlotForDate(dateKey) {
    const next = { ...(this.getHours() || {}) };
    delete next[dateKey];
    this.hours = next;
    return this;
  }

  /** Duree voulue d'une date, quel que soit le format stocke (nombre brut ou creneau). */
  hoursOf(dateKey) {
    const entry = this.getHours()?.[dateKey];
    if (entry == null) return null;
    if (typeof entry === 'number') return entry > 0 ? entry : null;
    return entry?.hours > 0 ? entry.hours : null;
  }

  slotOf(dateKey) {
    const entry = this.getHours()?.[dateKey];
    return entry?.start && entry?.end ? { start: entry.start, end: entry.end } : null;
  }

  /** WEA-163 - selection directe (mode horaire) : pas de choix de periode a faire. */
  toggleDate(dateKey) {
    const picked = this.getDatesPicked();
    if (picked.indexOf(dateKey) > -1) {
      this.datepicked = picked.filter((d) => d !== dateKey);
      const nextPeriod = { ...this.getPeriod() };
      delete nextPeriod[dateKey];
      this.period = nextPeriod;
      const nextHours = { ...this.getHours() };
      delete nextHours[dateKey];
      this.hours = nextHours;
      return this;
    }
    this.datepicked = [...picked, dateKey];
    this.period = { ...this.getPeriod(), [dateKey]: 0 };
    return this;
  }

  reset() {
    this.period = [];
    this.datepicked = [];
    this.hours = {};
  }

  // eslint-disable-next-line class-methods-use-this
  getDaysInMonth2(month, year) {
    let start = dayjs().year(year).month(month).startOf('month');

    let end = dayjs(start).endOf('month');
    // on se met à lundi suivant le premier jour du mois
    while (start.format('d') !== '1') {
      start = start.add(1, 'day');
    }
    // let nextMonday = (7 - parseInt(start.format('d'), 10)) + 1;

    // puis on se met à last lundi pour avoir une ligne calendar qui comprend le debut du mois
    start = start.subtract(7, 'day');

    // on se met au dernier dimanche du mois
    // nextMonday = (7 - parseInt(end.format('d'), 10));
    end = end.add((7 - parseInt(end.format('d'), 10)), 'day');
    return [start, end];
  }

  getDaysInMonth(month, year) {
    const result = this.getDaysInMonth2(month, year);
    let date = result[0];
    const max = result[1];
    const days = [];
    // on parcours jusquà la fin entre start et end
    let i = 0;
    while (date.diff(max) <= 0 && i < 50) {
      let active = true;
      if (date.month() !== month) {
        active = false;
      }
      const newDate = dayjs(date).startOf('day');
      const day = {
        date: newDate,
        active,
      };
      days.push(day);
      date = date.add(1, 'day');
      i += 1;
    }
    return days;
  }

  isPast(date) {
    // eslint-disable-next-line no-unused-vars
    const self = this;
    let today = dayjs();
    today = today.startOf('day');
    let o = dayjs(date);
    if (typeof date === 'string' || date instanceof String) {
      o = dayjs(date);
    }
    o = o.startOf('day');
    return (o.diff(today) < 0);
  }
}

export default CalendarStore;
